import { useId, useState } from "react";

import { usePromptCallPageQuery } from "@/api/previewInsights";
import { cn } from "@/lib/cn";
import {
  promptRowOf,
  type LabPromptRow,
  type LabRunModel,
} from "@/lib/labRunModel";
import { INVOCATION_LABELS } from "@/lib/previewPromptCall";
import {
  NO_PROMPT_FILTER,
  PROMPT_CALL_PAGE_LIMIT,
  PROMPT_FILTER_LABELS,
  axisValue,
  describeListing,
  isFiltered,
  promptFilterAxes,
  promptFilterQuery,
  readPromptCallListing,
  withAxis,
  type PromptFilter,
  type PromptFilterAxis,
  type PromptFilterOption,
} from "@/lib/previewPromptInspector";
import { PromptCallCard } from "@/routes/PreviewRunPromptCall";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { EmptyState } from "@/ui/states";

/**
 * Prompts: every prompt call of the run as one filterable list.
 *
 * The list is the server's (`usePromptCallPageQuery`): the server narrows,
 * counts the facets and cuts the pages, and this tab renders what comes back.
 * Filters are LOCAL state, never the address (`turn` is already the Lab's own
 * parameter). When the listing cannot be read the tab falls back to the
 * run's own `model.prompts` and filters those in the browser, and says so.
 *
 * A call's body is read only by `PromptCallCard`, after its own explicit press.
 */

type Props = {
  projectId: string;
  model: LabRunModel;
  selectedTurn: number | null;
  onSelectTurn: (turn: number) => void;
};

const AXES: readonly PromptFilterAxis[] = ["agent", "phase", "callType"];

function latencyText(ms: number | null): string {
  return ms === null ? "not recorded" : `${ms} ms`;
}

function outcomeBadge(row: LabPromptRow) {
  const label = INVOCATION_LABELS[row.outcome];
  const reason = row.reasonCode !== null ? ` (${row.reasonCode})` : "";
  return (
    <Badge
      tone={
        row.outcome === "invoked"
          ? "success"
          : row.outcome === "failed"
            ? "destructive"
            : "warning"
      }
    >
      {label}
      {reason}
    </Badge>
  );
}

function PromptRowItem({
  row,
  projectId,
  previewRunRef,
  onSelectTurn,
}: {
  row: LabPromptRow;
  projectId: string;
  previewRunRef: string;
  onSelectTurn: (turn: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <li className="rounded-md border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone="accent">{row.callType ?? "unknown type"}</Badge>
        {outcomeBadge(row)}
        <span className="text-muted-foreground">
          {row.agent ?? "agent not recorded"} ·{" "}
          {row.phase ?? "phase not recorded"} ·{" "}
          {row.turnNumber === null ? "no turn" : `turn ${row.turnNumber}`}
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {latencyText(row.latencyMs)} · {row.model ?? "model not carried"}
        {row.deployment !== null ? ` (${row.deployment})` : ""}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? "Close" : "Open"}
          <span className="sr-only">
            {" "}
            prompt call
            {row.sequence === null ? "" : `, sequence ${row.sequence}`}
          </span>
        </Button>
        {row.turnNumber !== null ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onSelectTurn(row.turnNumber as number)}
          >
            Show turn
            <span className="sr-only"> {row.turnNumber}</span>
          </Button>
        ) : null}
      </div>
      {open ? (
        <div id={panelId} className="mt-3">
          <PromptCallCard
            call={row.call}
            projectId={projectId}
            previewRunRef={previewRunRef}
            title="Prompt call"
          />
        </div>
      ) : null}
    </li>
  );
}

function AxisSelect({
  axis,
  options,
  active,
  onChange,
}: {
  axis: PromptFilterAxis;
  options: readonly PromptFilterOption[];
  active: string | null;
  onChange: (value: string | null) => void;
}) {
  const id = useId();
  const known =
    active === null || options.some((option) => option.value === active);
  return (
    <div className="min-w-32 flex-1 space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-foreground">
        {PROMPT_FILTER_LABELS[axis]}
      </label>
      <select
        id={id}
        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        value={active ?? ""}
        onChange={(event) => onChange(event.currentTarget.value || null)}
      >
        <option value="">All</option>
        {known ? null : <option value={active ?? ""}>{active}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label} ({option.count})
          </option>
        ))}
      </select>
    </div>
  );
}

function TurnInput({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (value: string | null) => void;
}) {
  const id = useId();
  return (
    <div className="w-24 space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-foreground">
        {PROMPT_FILTER_LABELS.turnNumber}
      </label>
      <input
        id={id}
        type="number"
        min={1}
        inputMode="numeric"
        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        value={value ?? ""}
        onChange={(event) => {
          const raw = event.currentTarget.value.trim();
          const parsed = Number(raw);
          onChange(
            raw !== "" && Number.isInteger(parsed) && parsed >= 1 ? raw : null,
          );
        }}
      />
    </div>
  );
}

/** Later pages of the server listing, each its own query so none is re-cut. */
function MorePage({
  projectId,
  previewRunRef,
  filter,
  afterSequence,
  isLast,
  onMore,
  onSelectTurn,
}: {
  projectId: string;
  previewRunRef: string;
  filter: PromptFilter;
  afterSequence: number;
  isLast: boolean;
  onMore: (next: number) => void;
  onSelectTurn: (turn: number) => void;
}) {
  const page = usePromptCallPageQuery(
    projectId,
    previewRunRef,
    { afterSequence, ...promptFilterQuery(filter) },
    { pollIntervalMs: false },
  );
  if (page.isPending) {
    return (
      <li className="text-sm text-muted-foreground">
        Loading more prompt calls…
      </li>
    );
  }
  const listing =
    page.data === undefined ? null : readPromptCallListing(page.data);
  if (page.isError || listing === null || listing.kind !== "listed") {
    return (
      <li className="text-sm text-destructive">
        <span role="alert">More prompt calls could not be read.</span>
      </li>
    );
  }
  return (
    <>
      {listing.calls.map((call) => (
        <PromptRowItem
          key={call.eventRef}
          row={promptRowOf(call)}
          projectId={projectId}
          previewRunRef={previewRunRef}
          onSelectTurn={onSelectTurn}
        />
      ))}
      {isLast && listing.hasMore && listing.nextAfterSequence !== null ? (
        <li>
          <Button
            variant="outline"
            onClick={() => onMore(listing.nextAfterSequence as number)}
          >
            Load more
          </Button>
        </li>
      ) : null}
    </>
  );
}

/** The local fallback's filter, applied to the run's own rows. */
function matchesLocal(row: LabPromptRow, filter: PromptFilter): boolean {
  return (
    (filter.turnNumber === null || row.turnNumber === filter.turnNumber) &&
    (filter.agent === null || row.agent === filter.agent) &&
    (filter.phase === null || row.phase === filter.phase) &&
    (filter.callType === null || row.family === filter.callType)
  );
}

function localOptions(
  rows: readonly LabPromptRow[],
  axis: PromptFilterAxis,
): PromptFilterOption[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const value =
      axis === "agent" ? row.agent : axis === "phase" ? row.phase : row.family;
    if (value !== null) counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([value, count]) => ({ value, label: value, count }));
}

export function LabPromptsTab({ projectId, model, onSelectTurn }: Props) {
  const headingId = useId();
  const [filter, setFilter] = useState<PromptFilter>(NO_PROMPT_FILTER);
  const [cursors, setCursors] = useState<number[]>([]);
  const previewRunRef = model.previewRunRef;
  const hasTurns = model.lab.turns.length > 0;

  const first = usePromptCallPageQuery(
    projectId,
    previewRunRef,
    promptFilterQuery(filter),
    { enabled: hasTurns, pollIntervalMs: false },
  );

  const change = (axis: PromptFilterAxis, value: string | null) => {
    setFilter((current) => withAxis(current, axis, value));
    setCursors([]);
  };
  const clear = () => {
    setFilter(NO_PROMPT_FILTER);
    setCursors([]);
  };

  const strip = (
    <ul
      aria-label="Prompt calls by family, whole run"
      className="flex flex-wrap gap-2"
    >
      {model.promptFamilies.map((entry) => (
        <li key={entry.family}>
          <Badge tone="outline">
            {entry.family} · {entry.count}
          </Badge>
        </li>
      ))}
    </ul>
  );

  if (!hasTurns) {
    return (
      <section aria-labelledby={headingId}>
        <h2
          id={headingId}
          className="mb-3 text-base font-semibold text-foreground"
        >
          Prompts
        </h2>
        <EmptyState
          title="No turns yet"
          description="Prompt calls appear once the run has recorded turns."
        />
      </section>
    );
  }

  const listing =
    first.data === undefined ? null : readPromptCallListing(first.data);
  const served = listing !== null && listing.kind === "listed" ? listing : null;
  const fallback = !first.isPending && served === null;

  const localRows = model.prompts.filter((row) => matchesLocal(row, filter));
  const axes = served
    ? promptFilterAxes(served.facets, served.filterSupport, filter)
    : null;
  const optionsFor = (axis: PromptFilterAxis): readonly PromptFilterOption[] =>
    axes?.find((state) => state.axis === axis)?.options ??
    localOptions(model.prompts, axis);
  const callTypeChips = optionsFor("callType");

  const lastCursor = cursors.at(-1);

  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <h2 id={headingId} className="text-base font-semibold text-foreground">
        Prompts
      </h2>
      {model.promptFamilies.length > 0 ? strip : null}

      <div className="space-y-3 rounded-md border border-border bg-card p-3">
        <div className="flex flex-wrap items-end gap-3">
          <TurnInput
            value={filter.turnNumber}
            onChange={(value) => change("turnNumber", value)}
          />
          {AXES.map((axis) => (
            <AxisSelect
              key={axis}
              axis={axis}
              options={optionsFor(axis)}
              active={axisValue(filter, axis)}
              onChange={(value) => change(axis, value)}
            />
          ))}
          {isFiltered(filter) ? (
            <Button variant="ghost" onClick={clear}>
              Clear filters
            </Button>
          ) : null}
        </div>
        {callTypeChips.length > 0 ? (
          <ul aria-label="Filter by call type" className="flex flex-wrap gap-2">
            {callTypeChips.map((option) => {
              const active = filter.callType === option.value;
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    aria-pressed={active}
                    onClick={() =>
                      change("callType", active ? null : option.value)
                    }
                    className={cn(
                      "rounded-md border px-2 py-0.5 text-xs font-medium focus-visible:outline-2 focus-visible:outline-ring",
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {option.label} · {option.count}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      {first.isPending ? (
        <p className="text-sm text-muted-foreground">Loading prompt calls…</p>
      ) : null}

      {served !== null ? (
        <>
          <p aria-live="polite" className="text-sm text-muted-foreground">
            {describeListing(served, filter)}
          </p>
          {served.calls.length > 0 ? (
            <ul className="space-y-2">
              {served.calls.map((call) => (
                <PromptRowItem
                  key={call.eventRef}
                  row={promptRowOf(call)}
                  projectId={projectId}
                  previewRunRef={previewRunRef}
                  onSelectTurn={onSelectTurn}
                />
              ))}
              {cursors.map((cursor) => (
                <MorePage
                  key={cursor}
                  projectId={projectId}
                  previewRunRef={previewRunRef}
                  filter={filter}
                  afterSequence={cursor}
                  isLast={cursor === lastCursor}
                  onMore={(next) => setCursors((current) => [...current, next])}
                  onSelectTurn={onSelectTurn}
                />
              ))}
              {cursors.length === 0 &&
              served.hasMore &&
              served.nextAfterSequence !== null ? (
                <li>
                  <Button
                    variant="outline"
                    onClick={() =>
                      setCursors([served.nextAfterSequence as number])
                    }
                  >
                    Load more
                  </Button>
                </li>
              ) : null}
            </ul>
          ) : null}
        </>
      ) : null}

      {fallback ? (
        <>
          <p role="status" className="text-sm text-muted-foreground">
            The prompt-call listing is{" "}
            {listing?.kind === "unreadable" ? "not readable" : "unavailable"},
            so this list is built from the events already loaded for this run
            and filtered in the browser ({PROMPT_CALL_PAGE_LIMIT}-call pages do
            not apply). Showing {localRows.length} of {model.prompts.length}{" "}
            prompt calls.
          </p>
          {localRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No prompt call matches this filter.
            </p>
          ) : (
            <ul className="space-y-2">
              {localRows.map((row) => (
                <PromptRowItem
                  key={row.eventRef}
                  row={row}
                  projectId={projectId}
                  previewRunRef={previewRunRef}
                  onSelectTurn={onSelectTurn}
                />
              ))}
            </ul>
          )}
        </>
      ) : null}
    </section>
  );
}
